import type React from "react";
import { useCallback, useEffect, useRef } from "react";
import { theme, inputStyle, floatingSheet, type as text } from "../../theme";
import { dateInputToIso } from "../../lib/dateOnly";

// ── Catalogues (mirror backend/src/lib/resilienceRules.ts) ────────────────────

export const INCIDENT_CATEGORIES: Array<{ id: string; label: string }> = [
  { id: "phishing", label: "Phishing" },
  { id: "malware", label: "Malware" },
  { id: "ransomware", label: "Ransomware / cyber-extortion" },
  { id: "business_email_compromise", label: "Business email compromise / payment fraud" },
  { id: "unauthorised_access", label: "Unauthorised access / account compromise" },
  { id: "data_breach", label: "Data breach / exfiltration" },
  { id: "denial_of_service", label: "Denial of service" },
  { id: "supply_chain", label: "Supply-chain compromise" },
  { id: "insider", label: "Insider misuse" },
  { id: "lost_or_stolen_device", label: "Lost or stolen device" },
  { id: "misconfiguration", label: "Misconfiguration / exposure" },
  { id: "vulnerability_exploited", label: "Vulnerability exploited" },
  { id: "social_engineering", label: "Social engineering" },
  { id: "physical", label: "Physical security" },
  { id: "system_failure", label: "System or service failure (non-malicious)" },
  { id: "other", label: "Other" },
];

export const EXERCISE_KINDS: Array<{ id: string; label: string }> = [
  { id: "tabletop", label: "Tabletop exercise" },
  { id: "incident_walkthrough", label: "Incident response walkthrough" },
  { id: "dr_failover", label: "Disaster recovery failover" },
  { id: "backup_restore", label: "Backup restoration test" },
  { id: "penetration_test", label: "Penetration test" },
  { id: "vulnerability_scan", label: "Vulnerability scan" },
  { id: "red_team", label: "Red team / adversary simulation" },
  { id: "phishing_simulation", label: "Phishing simulation" },
  { id: "supply_chain", label: "Supply-chain exercise" },
  { id: "crisis_communications", label: "Crisis communications test" },
  { id: "contingency_plan", label: "Contingency plan test" },
  { id: "other", label: "Other" },
];

export const TRAINING_KINDS: Array<{ id: string; label: string }> = [
  { id: "security_awareness", label: "Security awareness" },
  { id: "phishing_simulation", label: "Phishing simulation" },
  { id: "role_specific", label: "Role-specific training" },
  { id: "induction", label: "Induction" },
  { id: "board_briefing", label: "Board / leadership briefing" },
  { id: "incident_response", label: "Incident response training" },
  { id: "data_protection", label: "Data protection training" },
  { id: "certification", label: "Professional certification" },
  { id: "other", label: "Other" },
];

export const TRAINING_RESULTS: Array<{ id: string; label: string }> = [
  { id: "passed", label: "Passed" },
  { id: "attended", label: "Attended" },
  { id: "failed", label: "Failed" },
  { id: "clicked", label: "Clicked (phishing simulation)" },
  { id: "reported", label: "Reported (phishing simulation)" },
  { id: "not_completed", label: "Not completed" },
];

export const ASSET_KINDS: Array<{ id: string; label: string }> = [
  { id: "business_service", label: "Business service / essential function" },
  { id: "application", label: "Application" },
  { id: "system", label: "System" },
  { id: "server", label: "Server" },
  { id: "endpoint", label: "Endpoint / device" },
  { id: "network", label: "Network" },
  { id: "data", label: "Data set" },
  { id: "site_infrastructure", label: "Site infrastructure (power, cooling, connectivity)" },
  { id: "operational_technology", label: "Operational technology" },
  { id: "cloud_service", label: "Cloud service" },
  { id: "supplier_service", label: "Supplier-delivered service" },
  { id: "key_person", label: "Key person / knowledge" },
  { id: "other", label: "Other" },
];

export const SEVERITIES = ["low", "medium", "high", "critical"] as const;

/** GDPR Art. 33 / 34 risk assessment — mirrors DP_RISK_ASSESSMENTS in backend lib/resilienceRules.ts. */
export const DP_RISK_ASSESSMENTS: Array<{ id: string; label: string }> = [
  { id: "unassessed", label: "Not yet assessed" },
  { id: "unlikely", label: "Unlikely to result in a risk to individuals" },
  { id: "risk", label: "Likely to result in a risk to individuals" },
  { id: "high_risk", label: "Likely to result in a high risk to individuals" },
];
export const CRITICALITIES = ["low", "medium", "high", "critical"] as const;

export const labelOf = (list: Array<{ id: string; label: string }>, id: string) => list.find((x) => x.id === id)?.label ?? id;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface IncidentNotification {
  id: string;
  bodyKey: string;
  body: string;
  basis: string;
  required: boolean;
  windowHours: number | null;
  dueAt: string | null;
  status: "pending" | "sent" | "not_required" | "waived";
  sentAt: string | null;
  reference: string | null;
  method: string | null;
  notes: string | null;
}

export interface Incident {
  id: string;
  displayId: number | null;
  title: string;
  description: string;
  category: string;
  severity: string;
  status: "open" | "contained" | "resolved" | "closed";
  occurredAt: string | null;
  detectedAt: string;
  awareAt: string;
  containedAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  affectedUsers: number | null;
  durationMinutes: number | null;
  geographicScope: string | null;
  servicesAffected: string | null;
  personalDataInvolved: boolean;
  essentialServiceImpact: boolean;
  isSignificant: boolean;
  significanceRationale: string | null;
  /** Operating countries whose residents' data or establishments are affected; empty = not yet confirmed. */
  affectedJurisdictions: string[];
  /** US states whose residents' data is affected; empty = not yet confirmed (every state in scope assumed). */
  affectedUsStates?: string[];
  /** DP_RISK_ASSESSMENTS id; null = not yet assessed. */
  dpRiskAssessment: string | null;
  dataEncrypted: boolean | null;
  /** Individuals affected, when known (count triggers: HIPAA, GLBA, Singapore 500). */
  affectedIndividuals?: number | null;
  /** When the breach was assessed as notifiable (Singapore PDPA s.26D(1) counts from it). */
  dpNotifiableAssessedAt?: string | null;
  /** CRA Art. 14: the incident concerns a product with digital elements the organisation manufactures; null = not answered. */
  affectsDigitalProduct?: boolean | null;
  threatActor: string | null;
  attackVector: string | null;
  rootCause: string | null;
  lessonsLearned: string | null;
  improvements: string | null;
  // Post-incident review (PUT /resilience/incidents/:id/review)
  pirHeldAt?: string | null;
  pirAttendees?: string | null;
  pirWentWell?: string | null;
  pirToImprove?: string | null;
  ransomDemand: boolean;
  ransomPaid: boolean | null;
  sanctionsCheckedAt: string | null;
  lawEnforcementReportedAt: string | null;
  ownerName: string | null;
  linkedRiskId: string | null;
  linkedBreachId: string | null;
  linkedDoraIncidentId: string | null;
  supplierId: string | null;
  /** When the supplier told the organisation of the incident (checked against its agreement's window). */
  supplierNotifiedAt?: string | null;
  siteId: string | null;
  evidenceDocumentId: string | null;
  notifications: IncidentNotification[];
  assets: Array<{ asset: { id: string; name: string; criticality: string; kind: string } }>;
  /** What the linked assets take down, from the dependency graph (GET /resilience/incidents only). */
  impact?: IncidentImpact;
  linkedRisk: { id: string; title: string; displayId: number | null; status: string } | null;
  linkedBreach: { id: string; title: string; status: string; notifiedDpaAt: string | null } | null;
  linkedDoraIncident: { id: string; title: string; status: string; isMajor: boolean } | null;
  supplier: { id: string; name: string } | null;
  site: { id: string; name: string } | null;
  // The Sentinel incident / Defender alert the record was raised from (IncidentSources.tsx)
  sentinelIncidentId?: string | null;
  m365AlertId?: string | null;
  /** The linked records, tolerances and supplier notice (backend services/recordConsistency.ts); GET routes only. */
  consistency?: IncidentConsistency | null;
}

// ── One incident, one set of facts (backend lib/recordConsistency.ts) ──────────

export type DivergenceTarget = "breach" | "dora" | "claim";
export type DivergenceField = "rootCause" | "lessonsLearned" | "supplierId" | "assetIds" | "notifiedAt";

/** A linked record that now says something different from the incident. */
export interface Divergence {
  target: DivergenceTarget;
  recordId: string;
  field: DivergenceField;
  label: string;
  /** Display text; for `notifiedAt` an ISO instant. */
  incident: string | null;
  record: string | null;
  /** What "take from the incident" does: replace the record's value, or add the incident's assets to it. */
  action: "replace" | "add";
}

/** A business impact analysis the incident has taken past its MTPD or RTO. */
export interface ToleranceBreach {
  biaId: string;
  ref: string;
  name: string;
  hours: number;
  basis: "recorded" | "resolved" | "ongoing";
  mtpdHours: number | null;
  rtoHours: number | null;
  exceeded: "mtpd" | "rto";
  via: string[];
}

/** Whether the supplier told the organisation inside its agreement's window. */
export interface SupplierNoticeVerdict {
  state: "on_time" | "late" | "not_recorded" | "no_window";
  windowHours: number | null;
  contractTitle: string | null;
  clockFrom: string;
  clockBasis: "occurred" | "detected";
  dueAt: string | null;
  notifiedAt: string | null;
  lateByHours: number | null;
}

export interface IncidentConsistency {
  divergence: Divergence[];
  tolerance: ToleranceBreach[];
  supplierNotice: SupplierNoticeVerdict | null;
}

/** What a save also recorded elsewhere, or could not (backend services/recordSync.ts). */
export interface SyncResult { filled: string[]; skipped: string[] }

export interface Exercise {
  id: string;
  title: string;
  kind: string;
  scenario: string | null;
  scope: string | null;
  objectives: string | null;
  scheduledDate: string;
  completedDate: string | null;
  status: "planned" | "in_progress" | "completed" | "cancelled";
  participants: string | null;
  provider: string | null;
  outcome: "met" | "partially_met" | "not_met" | null;
  findingsCount: number | null;
  criticalFindings: number | null;
  /** What a restore / failover / contingency test achieved (hours); compared with each BIA's RTO / RPO. */
  recoveryTimeHours?: number | null;
  recoveryPointHours?: number | null;
  findings: string | null;
  lessonsLearned: string | null;
  actions: string | null;
  notes: string | null;
  evidenceDocumentId: string | null;
  // The incident whose lessons it tests; where it was run is the Office tag.
  basedOnIncidentId: string | null;
  basedOnIncident: { id: string; displayId: number | null; title: string; severity: string } | null;
  /** The scenario library entry it was built from (lib/exerciseScenarios.ts). */
  scenarioTemplate?: string | null;
  suppliers: Array<{ supplier: { id: string; name: string } }>;
  assets: Array<{ asset: { id: string; name: string; criticality: string } }>;
  scheduleEvent: { id: string; eventDate: string } | null;
  /** The DORA resilience test this exercise was run as (DoraResilienceTest.exerciseId). */
  doraTest?: { id: string; testType: string; status: string } | null;
}

export interface TrainingRecord {
  id: string;
  userId: string | null;
  personName: string | null;
  personEmail: string | null;
  kind: string;
  title: string;
  provider: string | null;
  completedAt: string;
  expiresAt: string | null;
  result: string | null;
  score: number | null;
  notes: string | null;
  user: { id: string; displayName: string | null; email: string } | null;
}

export interface Asset {
  id: string;
  name: string;
  kind: string;
  description: string | null;
  /** When the vendor stops supplying security updates (lib/assetLifecycle.ts). */
  supportEndsAt?: string | null;
  criticality: string;
  /** Classification (lib/assetClassification.ts): low | medium | high, null = not rated. */
  confidentiality?: string | null;
  integrity?: string | null;
  availability?: string | null;
  isEssentialFunction: boolean;
  ownerName: string | null;
  ownerUserId: string | null;
  siteId: string | null;
  supplierId: string | null;
  dependencies: string | null;
  rtoHours: number | null;
  rpoHours: number | null;
  contingencyPlan: boolean;
  contingencyTestedAt: string | null;
  status: "active" | "retired";
  source: string;
  externalId: string | null;
  sourceMeta: Record<string, unknown> | null;
  lastSeenAt: string | null;
  reviewedAt: string | null;
  tags: string[];
  owner: { id: string; displayName: string | null; email: string; isActive: boolean } | null;
  site: { id: string; name: string } | null;
  supplier: { id: string; name: string } | null;
  _count: { risks: number; incidents: number; exercises: number; dependsOn: number; dependents: number };
}

// ── Asset dependency graph (mirror of backend/src/lib/assetGraph.ts) ──────────

/** One side of a dependency edge as the asset detail lists it: the linked asset plus the link. */
export interface AssetLink {
  id: string;
  assetId: string;
  name: string;
  kind: string;
  criticality: string;
  isEssentialFunction: boolean;
  status: string;
  linkKind: string;
  note: string | null;
  /** manual (drawn by a member) or discovery (the delivered_by edge seeded from the asset's supplier link). */
  source: string;
}

export interface ImpactedAsset {
  id: string;
  name: string;
  kind: string;
  criticality: string;
  isEssentialFunction: boolean;
  /** 0 = the asset itself (an incident linked straight to a business service). */
  depth: number;
  via: string;
  path: Array<{ assetId: string; name: string; linkKind: string | null }>;
  /** "Payroll takes data from HR database, which is hosted on SQL01" (empty at depth 0). */
  pathText: string;
}

export interface ImpactResult { businessServices: ImpactedAsset[]; other: ImpactedAsset[]; truncated: boolean; maxDepth: number }

export interface IncidentImpact { businessServices: ImpactedAsset[]; otherCount: number; truncated: boolean }

// Mirror of lib/assetDiscovery.ts ASSET_SOURCE_LABELS (row badges; the discovery panel gets its labels from the API).
export const ASSET_SOURCE_LABELS: Record<string, string> = {
  intune: "Intune", defender: "Defender for Endpoint", m365: "Microsoft 365", google: "Google Workspace", aws: "AWS", gcp: "Google Cloud", firewall: "Firewall",
  sentinel: "Sentinel", storage: "Evidence storage", supplier: "Supplier register", ropa: "RoPA", site: "Site",
  import:   "Imported inventory",
};

export interface DiscoverySource {
  source: string;
  label: string;
  description: string;
  available: boolean;
  enabled: boolean;
  retires: boolean;
  counts: { active: number; retired: number; unreviewed: number };
  lastSeenAt: string | null;
  /** Cloud sources: whether the full inventory (Tagging API / Cloud Asset Inventory) is being read. */
  inventory?: { synced: boolean; lastSync: string | null; error: string | null; resources: number };
}

export interface DiscoveryView { lastRunAt: string | null; sources: DiscoverySource[] }

export interface DiscoveryRun {
  ranAt: string;
  created: number;
  updated: number;
  retired: number;
  sources: Array<{ source: string; label: string; available: boolean; enabled: boolean; found: number; created: number; updated: number; retired: number; reactivated: number }>;
}

export interface InsurancePolicy {
  id: string;
  insurer: string;
  broker: string | null;
  policyNumber: string | null;
  currency: string;
  coverLimit: number | null;
  excess: number | null;
  startsAt: string | null;
  expiresAt: string;
  notificationWindowHours: number | null;
  notificationContact: string | null;
  incidentResponseRetainer: boolean;
  retainerProvider: string | null;
  coversRansom: boolean | null;
  coversBusinessInterruption: boolean | null;
  coversRegulatoryFines: boolean | null;
  coversThirdPartyLiability: boolean | null;
  conditions: string | null;
  exclusions: string | null;
  notes: string | null;
  status: "active" | "expired" | "cancelled";
  /** The policy's latest record in the Evidence Repository (POST /resilience/insurance/:id/evidence). */
  evidenceDocumentId?: string | null;
}

export interface AttentionItem {
  id: string;
  type: string;
  title: string;
  description: string;
  href: string;
  severity: "high" | "medium" | "low";
  dueAt: string | null;
}

export interface PlaybookStep { key: string; title: string; detail: string; basis: string }

export interface ResilienceSummary {
  lastReviewed: string;
  counts: { incidentsOpen: number; incidentsYear: number; pendingNotifications: number; exercisesPlanned: number; exercisesCompletedYear: number; trainingRecords: number; assets: number; criticalAssets: number };
  insurance: { id: string; insurer: string; expiresAt: string; notificationWindowHours: number | null } | null;
  attention: AttentionItem[];
  context: {
    regulations: string[];
    operatingCountries: string[];
    /** Organization.usStates: the US states in scope, offered on an incident. */
    usStates?: string[];
    oesRegimes: Array<{ regulation: string; status: "required" | "conditional" }>;
    certSources: string[];
    doraMonitoringEnabled: boolean;
    tprmEnabled: boolean;
  };
  playbook: PlaybookStep[];
}

export interface Option { id: string; name: string }

// ── Formatting ────────────────────────────────────────────────────────────────

// The platform's one date format (lib/format.ts); fmtDay is the name these pages already use.
export { fmtDateTime, fmtDate as fmtDay } from "../../lib/format";

/** ISO → value for <input type="datetime-local"> (local time). */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local / date input value → ISO string (or null when empty). A date-only value
 *  (yyyy-mm-dd) becomes UTC midnight, the convention in lib/dateOnly.ts. */
export function fromInput(v: string): string | null {
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return dateInputToIso(v);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

// Date-only values (lib/dateOnly.ts): box, label and saved value agree on the UTC date.
export { toDateInput, dateInputToIso, fmtDateOnly, todayInput } from "../../lib/dateOnly";

export const nowLocalInput = () => toLocalInput(new Date().toISOString());

/** "in 3h", "2d ago", "due now". */
export function relativeDue(iso: string | null): string {
  if (!iso) return "no fixed clock";
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const unit = abs >= 48 * 3_600_000 ? `${Math.round(abs / 86_400_000)}d` : abs >= 3_600_000 ? `${Math.round(abs / 3_600_000)}h` : `${Math.max(1, Math.round(abs / 60_000))}m`;
  return ms < 0 ? `${unit} overdue` : `due in ${unit}`;
}

// ── Small UI pieces ───────────────────────────────────────────────────────────

const SEV_STYLE: Record<string, { bg: string; color: string }> = {
  critical: { bg: theme.color.riskCritBg, color: theme.color.riskCritText },
  high:     { bg: theme.color.riskHighBg, color: theme.color.riskHighText },
  medium:   { bg: theme.color.riskMedBg,  color: theme.color.riskMedText },
  low:      { bg: theme.color.riskLowBg,  color: theme.color.riskLowText },
};

export function SeverityPill({ value }: { value: string }) {
  const s = SEV_STYLE[value] ?? { bg: theme.color.panelAlt, color: theme.color.muted };
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 2, background: s.bg, color: s.color, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>{value}</span>;
}

const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  open:        { bg: theme.color.dangerBg,  color: theme.color.dangerText,  label: "Open" },
  contained:   { bg: theme.color.warningBg, color: theme.color.warningText, label: "Contained" },
  resolved:    { bg: theme.color.accentBg,  color: theme.color.accentText,  label: "Resolved" },
  closed:      { bg: theme.color.successBg, color: theme.color.successText, label: "Closed" },
  planned:     { bg: theme.color.accentBg,  color: theme.color.accentText,  label: "Planned" },
  in_progress: { bg: theme.color.warningBg, color: theme.color.warningText, label: "In progress" },
  completed:   { bg: theme.color.successBg, color: theme.color.successText, label: "Completed" },
  cancelled:   { bg: theme.color.panelAlt,  color: theme.color.muted,       label: "Cancelled" },
  pending:     { bg: theme.color.warningBg, color: theme.color.warningText, label: "Pending" },
  sent:        { bg: theme.color.successBg, color: theme.color.successText, label: "Sent" },
  not_required:{ bg: theme.color.panelAlt,  color: theme.color.muted,       label: "Not required" },
  waived:      { bg: theme.color.panelAlt,  color: theme.color.muted,       label: "Waived" },
  active:      { bg: theme.color.successBg, color: theme.color.successText, label: "Active" },
  expired:     { bg: theme.color.dangerBg,  color: theme.color.dangerText,  label: "Expired" },
  retired:     { bg: theme.color.panelAlt,  color: theme.color.muted,       label: "Retired" },
  overdue:     { bg: theme.color.dangerBg,  color: theme.color.dangerText,  label: "Overdue" },
  // Regulatory submissions (pages/SubmissionsPage.tsx). The ladder is draft -> ready to send ->
  // sent -> the authority confirmed: "ready" warns because it is waiting on someone here.
  draft:        { bg: theme.color.panelAlt,  color: theme.color.muted,       label: "Draft" },
  ready:        { bg: theme.color.warningBg, color: theme.color.warningText, label: "Ready to send" },
  submitted:    { bg: theme.color.accentBg,  color: theme.color.accentText,  label: "Submitted" },
  acknowledged: { bg: theme.color.successBg, color: theme.color.successText, label: "Acknowledged" },
  withdrawn:    { bg: theme.color.panelAlt,  color: theme.color.muted,       label: "Withdrawn" },
  // Corrective actions (pages/ImprovementRegister.tsx). Nothing reaches "verified" except from
  // "completed" — completion is the doer's word, verification is someone else's (ISO 27001 10.2).
  proposed:     { bg: theme.color.panelAlt,  color: theme.color.muted,       label: "Proposed" },
  approved:     { bg: theme.color.accentBg,  color: theme.color.accentText,  label: "Approved" },
  blocked:      { bg: theme.color.warningBg, color: theme.color.warningText, label: "Blocked" },
  verified:     { bg: theme.color.successBg, color: theme.color.successText, label: "Verified" },
};

export function StatusPill({ value, label }: { value: string; label?: string }) {
  const s = STATUS_STYLE[value] ?? { bg: theme.color.panelAlt, color: theme.color.muted, label: value };
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{label ?? s.label}</span>;
}

/**
 * The shared dialog. Escape and a click on the backdrop close it, but once anything has been typed
 * or chosen inside it they ask first, so a stray click never throws away a half-filled form. The
 * header's × button is a deliberate close and does not ask.
 */
/** Open dialogs, innermost last: Escape closes only the one on top. */
const openDialogs: object[] = [];

export function ModalOverlay({ children, onClose, wide }: { children: React.ReactNode; onClose?: () => void; wide?: boolean }) {
  const dirty = useRef(false);
  const downOnBackdrop = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const close = useCallback(() => {
    if (!onCloseRef.current) return;
    if (dirty.current && !window.confirm("Discard what you have entered?")) return;
    onCloseRef.current();
  }, []);
  useEffect(() => {
    const me = {};
    openDialogs.push(me);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || openDialogs[openDialogs.length - 1] !== me) return;
      e.preventDefault();
      close();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const i = openDialogs.indexOf(me);
      if (i >= 0) openDialogs.splice(i, 1);
    };
  }, [close]);
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (e.target === e.currentTarget && downOnBackdrop.current) close(); }}
    >
      <div
        role="dialog" aria-modal="true"
        style={{ ...floatingSheet, width: "100%", maxWidth: wide ? 900 : 620, maxHeight: "92vh", overflowY: "auto" }}
        onInput={() => { dirty.current = true; }}
        onChange={() => { dirty.current = true; }}
      >
        {children}
      </div>
    </div>
  );
}

export function ModalHeader({ title, onClose, right }: { title: React.ReactNode; onClose: () => void; right?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 20px", borderBottom: "1px solid var(--c-line)", position: "sticky", top: 0, background: "var(--c-panel)", zIndex: 1 }}>
      <div style={{ ...text.sectionTitle, margin: 0, minWidth: 0 }}>{title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {right}
        <button type="button" onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", fontSize: 20, lineHeight: 1 }}>×</button>
      </div>
    </div>
  );
}

export function Field({ label, children, span2, help }: { label: string; children: React.ReactNode; span2?: boolean; help?: string }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: text.body.fontSize, gridColumn: span2 ? "1 / -1" : undefined, minWidth: 0 }}>
      <span style={{ ...text.label, marginBottom: 0 }}>{label}</span>
      {children}
      {help && <span style={text.hint}>{help}</span>}
    </label>
  );
}

export const input: React.CSSProperties = { ...inputStyle, fontSize: text.body.fontSize };
export const textarea: React.CSSProperties = { ...inputStyle, fontSize: text.body.fontSize, resize: "vertical" };

export function Check({ checked, onChange, label, help }: { checked: boolean; onChange: (v: boolean) => void; label: string; help?: string }) {
  return (
    <label style={{ display: "flex", alignItems: "flex-start", gap: 8, ...text.body, cursor: "pointer" }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 3 }} />
      <span>{label}{help && <span style={{ ...text.hint, display: "block" }}>{help}</span>}</span>
    </label>
  );
}

export function Tri({ value, onChange }: { value: boolean | null; onChange: (v: boolean | null) => void }) {
  const opts: Array<{ v: boolean | null; label: string }> = [{ v: true, label: "Yes" }, { v: false, label: "No" }, { v: null, label: "Not sure" }];
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {opts.map((o) => (
        <button key={o.label} type="button" onClick={() => onChange(o.v)} style={{
          padding: "4px 10px", fontSize: text.hint.fontSize, borderRadius: 3, cursor: "pointer",
          border: value === o.v ? `1px solid ${theme.color.accent}` : "1px solid var(--c-border)",
          background: value === o.v ? "var(--c-accentBg)" : "transparent", color: value === o.v ? "var(--c-accentText)" : theme.color.muted, fontWeight: value === o.v ? 600 : 400,
        }}>{o.label}</button>
      ))}
    </div>
  );
}

export function MultiPick({ options, selected, onChange, empty }: { options: Option[]; selected: string[]; onChange: (ids: string[]) => void; empty?: string }) {
  if (options.length === 0) return <span style={text.hint}>{empty ?? "None available."}</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <button key={o.id} type="button" onClick={() => onChange(on ? selected.filter((x) => x !== o.id) : [...selected, o.id])} style={{
            padding: "3px 10px", borderRadius: 3, fontSize: text.hint.fontSize, cursor: "pointer",
            border: on ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)", background: on ? "var(--c-accentBg)" : "var(--c-panel)", color: on ? "var(--c-accentText)" : theme.color.muted, fontWeight: on ? 600 : 400,
          }}>{o.name}</button>
        );
      })}
    </div>
  );
}

export const secondaryBtn: React.CSSProperties = {
  background: "transparent", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "6px 12px", fontSize: text.hint.fontSize, color: theme.color.muted, cursor: "pointer", whiteSpace: "nowrap",
};

export const dangerBtn: React.CSSProperties = { ...secondaryBtn, color: theme.color.dangerText, borderColor: theme.color.danger };

/** A register table's header row: sentence-case column names in the label style, never uppercase. */
export const tableHeadRow: React.CSSProperties = { textAlign: "left", fontSize: text.label.fontSize, color: text.label.color };

export function EmptyState({ title, detail, action }: { title: string; detail: string; action?: React.ReactNode }) {
  return (
    <div style={{ padding: "36px 20px", textAlign: "center" }}>
      <div style={{ ...text.itemTitle, marginBottom: 6 }}>{title}</div>
      <div style={{ ...text.body, color: theme.color.muted, maxWidth: 560, margin: "0 auto 14px" }}>{detail}</div>
      {action}
    </div>
  );
}

/**
 * "Showing only what touches <asset> · Show everything" — the banner a register
 * carries when the asset register linked through to it (?asset=<id>). The asset's
 * name comes from the rows themselves, so no extra request is made to name it.
 */
export function AssetFilterChip({ assetName, count, noun, onClear }: { assetName: string | null; count: number; noun: string; onClear: () => void }) {
  return (
    <div style={{ padding: "8px 12px", marginBottom: 12, borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", background: "var(--c-accentBg)", border: "1px solid var(--c-accent)" }}>
      <span style={{ ...text.hint, color: "var(--c-accentText)" }}>
        {assetName
          ? <>Showing the {count} {count === 1 ? noun : `${noun}s`} that name <strong>{assetName}</strong> in the asset register.</>
          : <>That asset has no {noun} linked to it yet.</>}
      </span>
      <button type="button" onClick={onClear} style={secondaryBtn}>Show everything</button>
    </div>
  );
}

export function KeyValue({ items }: { items: Array<[string, React.ReactNode]> }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 200px) 1fr", rowGap: 6, columnGap: 12, alignItems: "baseline" }}>
      {items.map(([k, v]) => (
        <div key={k} style={{ display: "contents" }}>
          <div style={{ ...text.label, marginBottom: 0 }}>{k}</div>
          <div style={{ ...text.body, minWidth: 0, overflowWrap: "anywhere" }}>{v ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}
